import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PayrollPipeline } from '../../components/PayrollPipeline';
import { useReconcile, useEscalations, useEmployees, usePayrollRuns, useResolveEscalation, useManualAttendanceEntry } from '../../hooks';
import { RefreshCw, CheckCircle, X } from 'lucide-react';
import type { Escalation } from '@payroll/shared';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

function getLast6Periods(): string[] {
  const periods: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    periods.push(`${y}-${m}`);
    d.setMonth(d.getMonth() - 1);
  }
  return periods;
}

interface ReconcileResult {
  // 🚨 ชื่อต้องตรงกับที่ API ส่งมาเป๊ะ — ของเดิมประกาศเป็น `processed` แต่ API ส่ง
  // `employees_processed` → จอขึ้น "ประมวลผลพนักงาน:  คน" ค่าว่างมาตลอดโดยไม่มี error
  // (TypeScript จับไม่ได้เพราะจุดรับค่า cast เป็น ReconcileResult ตรง ๆ)
  employees_processed: number;
  escalations_created: number;
  projection_warning: string | null;
  payroll_reset: number;
}

const ESC_TYPE_LABEL: Record<string, string> = {
  short_work: 'ทำงานไม่ครบเวลา',
  late: 'เข้างานสาย',
  late_warning: 'สายมาก — ต้องออกใบเตือน',
  absent_no_evidence: 'ขาดงาน (ไม่มีหลักฐาน)',
  over_leave_quota: 'ลาเกินสิทธิ์',
  checkin_geofence_failed: 'เช็คอินนอกพื้นที่',
  conditional_evidence_overdue: 'เลยกำหนดส่งหลักฐาน',
  profile_incomplete: 'ประวัติไม่ครบ',
  missing_break_scan: 'ไม่สแกนพักเที่ยง',
  missing_clock_out: 'ไม่สแกนออกงาน',
  emergency_leave_review: 'ลาฉุกเฉินรอตัดสิน',
  no_return_followup: 'ไม่กลับมาทำงาน',
  ot_mismatch: 'OT ไม่ตรงเวลาจริง',
  ot_unrequested: 'OT ไม่ได้ขออนุมัติ',
  unscheduled_work: 'มาทำงานวันที่ไม่ได้กำหนด',
};
const escTypeLabel = (t: string) => ESC_TYPE_LABEL[t] ?? t;

/** Content-only version used by the PayrollCycle hub. */
export function OwnerReconcileContent() {
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const reconcile = useReconcile();
  const { data: escalations } = useEscalations() as { data: Escalation[] | undefined };
  const { data: employees } = useEmployees() as { data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null }> | undefined };
  // M1 audit — งวดที่ปิด/จ่ายเงินแล้ว (payroll_runs.status locked+) ต้องปิดปุ่ม
  // "เริ่มสรุปเวลา" ก่อนกด ไม่ใช่รอให้ 409 มาจาก server เท่านั้น. ใช้ endpoint เดิม
  // ที่มีอยู่แล้ว (GET /owner/payroll/run?period=) — ไม่เพิ่ม endpoint ใหม่.
  // R6 — endpoint คืน { rows, run_warnings, review_rev_mismatch } แล้ว (เดิมเป็น array)
  const { data: payrollRunsRes } = usePayrollRuns(period) as { data: { rows?: Array<{ status: string }> } | undefined };
  const payrollRuns = payrollRunsRes?.rows;
  const LOCKED_STATUSES = new Set(['locked', 'pdf_ready', 'sending', 'partial_sent', 'sent']);
  const periodLocked = (payrollRuns ?? []).some((r) => LOCKED_STATUSES.has(r.status));
  const empName = new Map((employees ?? []).map((e) => [e.emp_code, `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '')]));
  const nameOf = (code: string) => empName.get(code) ?? code;
  const toast = useToast();
  const confirm = useConfirm();
  const resolveEsc = useResolveEscalation();
  const manualEntry = useManualAttendanceEntry();

  // การ์ดที่กำลังแก้ (modal)
  const [selected, setSelected] = useState<Escalation | null>(null);
  const [entryTime, setEntryTime] = useState('');
  const [entryReason, setEntryReason] = useState('');
  // ประเภทที่ "ลงเวลาจริง" แก้ได้ → map เป็น kind ของ attendance
  const FIXABLE_KIND: Record<string, 'morning_in' | 'evening_out'> = {
    absent_no_evidence: 'morning_in',
    missing_clock_out: 'evening_out',
  };
  const openFix = (e: Escalation) => { setSelected(e); setEntryTime(''); setEntryReason(''); };
  const closeFix = () => { setSelected(null); };

  async function handleManualEntry() {
    if (!selected) return;
    const kind = FIXABLE_KIND[selected.type];
    if (!kind || !entryTime || !entryReason.trim()) { toast.error('กรอกเวลาจริง + เหตุผลให้ครบ'); return; }
    try {
      await manualEntry.mutateAsync({ emp_code: selected.emp_code, date: selected.date, kind, time: entryTime, reason: entryReason.trim() });
      toast.success('ลงเวลาจริงแล้ว — ปิดรายการตรวจสอบ');
      closeFix();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  async function handleResolve() {
    if (!selected) return;
    try {
      await resolveEsc.mutateAsync({ esc_id: selected.esc_id, resolution: 'resolved', note: entryReason.trim() || undefined });
      toast.success('ปิดรายการแล้ว');
      closeFix();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'ปิดรายการไม่สำเร็จ'); }
  }

  const pendingEsc = (escalations ?? []).filter((e) => e.status === 'pending');

  async function handleRun() {
    if (!(await confirm({ title: 'สรุปเวลาเข้างาน', message: `รันสรุปเวลางวด ${period}?` }))) return;
    try {
      const res = await reconcile.mutateAsync({ period });
      setResult(res as ReconcileResult);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <>
      <PayrollPipeline current="reconcile" period={period} />
      <div className="card p-4 mb-4">
        <label className="label">เลือกงวด</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input mb-3">
          {getLast6Periods().map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <button onClick={handleRun} disabled={reconcile.isPending || periodLocked} className="btn-primary w-full">
          <RefreshCw className={`w-4 h-4 ${reconcile.isPending ? 'animate-spin' : ''}`} />
          {reconcile.isPending ? 'กำลังประมวลผล...' : 'เริ่มสรุปเวลา'}
        </button>
        {periodLocked && (
          <p className="text-xs text-gray-500 mt-2">
            งวดนี้ปิดและคิดเงินไปแล้ว — ตัวเลขถูกตรึงไว้เพื่อการตรวจสอบ รันสรุปเวลาใหม่ไม่ได้
            ถ้าจำเป็นต้องแก้ไขตัวเลขงวดนี้ ต้องปลดล็อกงวดก่อน
          </p>
        )}
      </div>

      {result && (
        <div className="card p-4 bg-green-50 border-green-200 mb-4">
          <p className="font-medium text-green-800 mb-1 flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-[#2f7d52]" /> สรุปเวลาเสร็จแล้ว
          </p>
          <p className="text-sm text-green-700">ประมวลผลพนักงาน: {result.employees_processed} คน</p>
          <p className="text-sm text-green-700">พบรายการผิดปกติใหม่: {result.escalations_created} รายการ</p>
          {result.projection_warning && (
            <p className="text-sm text-green-700 mt-1">{result.projection_warning}</p>
          )}
          {result.payroll_reset > 0 && (
            <p className="text-sm text-amber-700 mt-1">
              ร่างเงินเดือนที่เคยอนุมัติไว้ถูกดึงกลับเป็นร่างแล้ว {result.payroll_reset} งวด — ต้องรันร่างใหม่และอนุมัติอีกครั้ง
            </p>
          )}
        </div>
      )}

      {pendingEsc.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">รายการที่ต้องตรวจสอบ ({pendingEsc.length})</p>
          <div className="space-y-2">
            {pendingEsc.map((e) => (
              <button key={e.esc_id} onClick={() => openFix(e)} className="card p-3 w-full text-left hover:bg-gray-50 transition-colors">
                <div className="flex justify-between">
                  <p className="font-medium text-sm">{nameOf(e.emp_code)}</p>
                  <span className="badge-warn">{escTypeLabel(e.type)}</span>
                </div>
                <p className="text-xs text-gray-600">{e.date}</p>
                {e.detail && <p className="text-xs text-gray-500 mt-1">{e.detail}</p>}
                <p className="text-[11px] text-[#c2185b] mt-1">แตะเพื่อแก้ไข →</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={closeFix}>
          <div className="card w-full sm:max-w-md p-4 rounded-t-2xl sm:rounded-2xl" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold">{nameOf(selected.emp_code)}</p>
                <p className="text-xs text-gray-500">{escTypeLabel(selected.type)} · {selected.date}</p>
              </div>
              <button onClick={closeFix} className="p-1 text-gray-400"><X className="w-5 h-5" /></button>
            </div>
            {selected.detail && <p className="text-xs text-gray-500 mb-3">{selected.detail}</p>}

            {FIXABLE_KIND[selected.type] && (
              <div className="mb-3 space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  ลงเวลาจริง ({selected.type === 'absent_no_evidence' ? 'เวลาเข้างาน' : 'เวลาออกงาน'})
                </p>
                <input type="time" value={entryTime} onChange={(ev) => setEntryTime(ev.target.value)} className="input" />
                <input type="text" value={entryReason} onChange={(ev) => setEntryReason(ev.target.value)} placeholder="เหตุผล (เช่น ลืมสแกน / GPS ไม่ผ่าน)" className="input" />
                <button onClick={handleManualEntry} disabled={manualEntry.isPending} className="btn-primary w-full">
                  {manualEntry.isPending ? 'กำลังบันทึก...' : 'บันทึกเวลาจริง + ปิดรายการ'}
                </button>
              </div>
            )}

            <div className="border-t pt-3">
              {!FIXABLE_KIND[selected.type] && (
                <input type="text" value={entryReason} onChange={(ev) => setEntryReason(ev.target.value)} placeholder="หมายเหตุ (ไม่บังคับ)" className="input mb-2" />
              )}
              <button onClick={handleResolve} disabled={resolveEsc.isPending} className="btn-secondary w-full">
                {resolveEsc.isPending ? 'กำลังปิด...' : 'ทำเครื่องหมายว่าตรวจ/แก้แล้ว (ปิดรายการ)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Standalone page kept for legacy /owner/reconcile (now redirects). */
export function OwnerReconcile() {
  return (
    <AdminLayout title="สรุปเวลาเข้างาน">
      <OwnerReconcileContent />
    </AdminLayout>
  );
}
